/**
 * El backoffice — `OD-0020` D-01/D-02 y la enmienda del 2026-09-28 (E-01).
 *
 * Seis vistas y nada más: empresas, ficha de una empresa, apoderamientos por
 * comprobar, clientes fundadores, las métricas de José (`ADR-0024`, solo
 * lectura y sin ids) y los correos enviados (`OD-0066`, solo lectura, con la
 * dirección enmascarada). Las escrituras son las que cada decisión
 * admite: validar o rechazar un apoderamiento (enmienda del 2026-09-21), las de
 * la ficha, y marcar que la llamada de un fundador se hizo (`OD-0059` D-06).
 *
 * Para quien no es operador, la API responde **404**, y esta aplicación no
 * distingue ese caso de "no hay sesión": no se confirma que exista un
 * backoffice ni que un correo esté o no en la lista.
 *
 * Navegación con `history`, sin router: tres rutas no justifican una
 * dependencia, y una dependencia menos en la herramienta que mira datos de
 * clientes reales es una decisión, no una pereza.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api/cliente";
import { t } from "./textos";
import { Acceso } from "./pantallas/Acceso";
import { Apoderamientos } from "./pantallas/Apoderamientos";
import { Correos } from "./pantallas/Correos";
import { Fundadores } from "./pantallas/Fundadores";
import { MetricasJose } from "./pantallas/MetricasJose";
import { Empresa } from "./pantallas/Empresa";
import { Empresas } from "./pantallas/Empresas";

type Vista =
  | { readonly tipo: "empresas" }
  | { readonly tipo: "empresa"; readonly id: string }
  | { readonly tipo: "apoderamientos" }
  | { readonly tipo: "fundadores" }
  | { readonly tipo: "jose" }
  | { readonly tipo: "correos" };

const PREFIJO_EMPRESA = "/empresa/";

export function rutaDe(pathname: string): Vista {
  if (pathname === "/apoderamientos") return { tipo: "apoderamientos" };
  if (pathname === "/fundadores") return { tipo: "fundadores" };
  if (pathname === "/jose") return { tipo: "jose" };
  if (pathname === "/correos") return { tipo: "correos" };
  if (pathname.startsWith(PREFIJO_EMPRESA)) {
    const id = decodeURIComponent(pathname.slice(PREFIJO_EMPRESA.length));
    if (id !== "") return { tipo: "empresa", id };
  }
  return { tipo: "empresas" };
}

export function App() {
  const [sesion, setSesion] = useState<"comprobando" | "dentro" | "fuera">("comprobando");
  const [vista, setVista] = useState<Vista>(() => rutaDe(window.location.pathname));

  const comprobar = useCallback(async (): Promise<void> => {
    const r = await api.sesion();
    setSesion(r.ok ? "dentro" : "fuera");
  }, []);

  useEffect(() => {
    void comprobar();
  }, [comprobar]);

  useEffect(() => {
    const alVolver = (): void => setVista(rutaDe(window.location.pathname));
    window.addEventListener("popstate", alVolver);
    return () => window.removeEventListener("popstate", alVolver);
  }, []);

  const ir = useCallback((ruta: string): void => {
    window.history.pushState(null, "", ruta);
    setVista(rutaDe(ruta));
  }, []);

  async function salir(): Promise<void> {
    await api.salir();
    setSesion("fuera");
  }

  if (sesion === "comprobando") return <p className="contenido suave">{t.comun.cargando}</p>;
  if (sesion === "fuera") return <Acceso onEntrado={() => setSesion("dentro")} />;

  return (
    <>
      <div className="cinta">
        <strong>{t.cinta.titulo}</strong>
        <span>{t.cinta.aviso}</span>
        <nav>
          <button type="button" data-variante="secundario" onClick={() => ir("/")}>
            {t.cinta.empresas}
          </button>
          <button type="button" data-variante="secundario" onClick={() => ir("/apoderamientos")}>
            {t.cinta.apoderamientos}
          </button>
          <button type="button" data-variante="secundario" onClick={() => ir("/fundadores")}>
            {t.cinta.fundadores}
          </button>
          <button type="button" data-variante="secundario" onClick={() => ir("/jose")}>
            {t.cinta.jose}
          </button>
          <button type="button" data-variante="secundario" onClick={() => ir("/correos")}>
            {t.cinta.correos}
          </button>
          <button type="button" data-variante="secundario" onClick={() => void salir()}>
            {t.cinta.salir}
          </button>
        </nav>
      </div>
      <main className="contenido">
        {vista.tipo === "empresas" ? <Empresas onAbrir={(id) => ir(`${PREFIJO_EMPRESA}${encodeURIComponent(id)}`)} /> : null}
        {vista.tipo === "empresa" ? <Empresa id={vista.id} onVolver={() => ir("/")} /> : null}
        {vista.tipo === "apoderamientos" ? <Apoderamientos /> : null}
        {vista.tipo === "fundadores" ? <Fundadores /> : null}
        {vista.tipo === "jose" ? <MetricasJose /> : null}
        {vista.tipo === "correos" ? <Correos onAbrirEmpresa={(id) => ir(`${PREFIJO_EMPRESA}${encodeURIComponent(id)}`)} /> : null}
      </main>
    </>
  );
}
